import React, { useEffect, useMemo, useRef, useState } from "react";

/**
 * RatingsWidgetPro – premium, animated, accessible
 * - Hero score with count-up + star fill sweep
 * - Animated sub-score bars with subtle shimmer
 * - Pull-quote card that fades in
 * - Trust badges row
 * - Tailwind-only, no external libs, reduced-motion friendly
 */

export default function RatingsWidgetPro() {
  const { ref, inViewOnce } = useInViewOnce({ threshold: 0.35 });
  const reduced = usePrefersReducedMotion();

  const play = inViewOnce && !reduced;

  const subs = useMemo(
    () => [
      { label: "Qualität", value: 98 },
      { label: "Pünktlichkeit", value: 96 },
      { label: "Freundlichkeit", value: 99 },
    ],
    []
  );

  return (
    <section ref={ref} className="relative isolate py-16 sm:py-20 bg-white">
      {/* soft gradient halo */}
      <div className="pointer-events-none absolute inset-0 -z-10 opacity-60">
        <div className="absolute left-1/2 top-1/2 h-[520px] w-[520px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-gradient-to-tr from-emerald-200/40 via-emerald-100/30 to-transparent blur-3xl" />
      </div>

      <div className="mx-auto max-w-6xl px-6 lg:px-12">
        <header className="mb-10 text-center">
          <h3 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-gray-900">
            Kundenzufriedenheit auf Top-Niveau
          </h3>
          <p className="mt-3 text-sm sm:text-base text-gray-600">
            Echte Bewertungen, echtes Vertrauen.
          </p>
        </header>

        <div className="grid items-center gap-8 lg:gap-12 sm:grid-cols-2">
          {/* LEFT: hero score */}
          <div className="justify-self-center sm:justify-self-start">
            <div className="flex items-end gap-3">
              <BigScore play={play} />
              <span className="mb-1 text-sm sm:text-base text-gray-500">von 5.0</span>
            </div>
            <StarsRow value={4.9} play={play} />

            <p className="mt-2 text-sm sm:text-base text-gray-600">
              Basierend auf <strong className="tabular-nums">{play ? <CountUp end={128} /> : 128}</strong> verifizierten Bewertungen
            </p>

            {/* pull-quote */}
            <blockquote
              className={[
                "mt-6 w-full max-w-md rounded-2xl border border-gray-100 bg-white/80 p-4 shadow-sm backdrop-blur",
                play ? "opacity-100 translate-y-0" : "opacity-0 translate-y-2",
              ].join(" ")}
              style={{ transition: "opacity 600ms 120ms, transform 600ms 120ms" }}
            >
              <div className="flex items-start gap-3">
                <QuoteIcon className="h-5 w-5 text-emerald-600" />
                <p className="text-sm leading-relaxed text-gray-700">
                  "Schnell, gründlich und super freundlich – unsere Räume glänzen nach jedem Einsatz!"
                </p>
              </div>
              <footer className="mt-2 text-xs text-gray-500">— M. Schneider (Google Review)</footer>
            </blockquote>
          </div>

          {/* RIGHT: bars */}
          <div className="grid gap-4">
            {subs.map((it, i) => (
              <Bar key={it.label} label={it.label} value={it.value} delay={i * 120} play={play} />
            ))}

            {/* badges */}
            <div className="mt-2 grid grid-cols-2 gap-3 sm:grid-cols-4">
              <Badge label="Google Reviews" />
              <Badge label="Trusted Local" />
              <Badge label="Eco Friendly" />
              <Badge label="Fully Insured" />
            </div>
          </div>
        </div>
      </div>

      <style>{extraCSS}</style>
    </section>
  );
}

/* ============ Pieces ============ */

function BigScore({ play }) {
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!play) { setVal(4.9); return; }
    const start = 4.2, end = 4.9, dur = 1100;
    let raf = 0; const t0 = performance.now();
    const ease = (t) => 1 - Math.pow(1 - t, 4);
    const step = (now) => {
      const t = Math.min(1, (now - t0) / dur);
      const v = start + (end - start) * ease(t);
      setVal(Math.round(v * 10) / 10);
      if (t < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [play]);
  return (
    <span className="text-5xl sm:text-6xl font-black tabular-nums text-gray-900">
      {val.toFixed(1)}
    </span>
  );
}

function CountUp({ end = 120, duration = 900 }) {
  const [n, setN] = useState(0);
  useEffect(() => {
    let raf = 0; const t0 = performance.now();
    const ease = (t) => 1 - Math.pow(1 - t, 3);
    const step = (now) => {
      const t = Math.min(1, (now - t0) / duration);
      setN(Math.round(end * ease(t)));
      if (t < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [end, duration]);
  return <>{n}+</>;
}

function StarsRow({ value = 4.9, play }) {
  const pct = Math.max(0, Math.min(100, (value / 5) * 100));
  return (
    <div className="relative mt-2 inline-block">
      {/* empty */}
      <div className="flex gap-[6px] text-gray-300">{Array.from({ length: 5 }).map((_, i) => <Star key={i} />)}</div>
      {/* gold mask */}
      <div
        className="stars-mask absolute inset-0 overflow-hidden pointer-events-none"
        style={{ width: `${pct}%`, transition: play ? "width 900ms cubic-bezier(.2,.7,.2,1) 150ms" : "none" }}
      >
        <div className="flex gap-[6px] text-yellow-400">{Array.from({ length: 5 }).map((_, i) => <Star key={i} />)}</div>
      </div>
    </div>
  );
}
function Star() {
  return (
    <svg className="h-6 w-6" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M12 17.27l6.18 3.73-1.64-7.03L21 9.24l-7.19-.62L12 2 10.19 8.62 3 9.24l4.46 4.73-1.64 7.03L12 17.27z" />
    </svg>
  );
}

function Bar({ label, value, delay = 0, play }) {
  return (
    <div>
      <div className="mb-1 flex justify-between text-sm text-gray-600">
        <span>{label}</span>
        <span className="font-semibold text-gray-900">{value}%</span>
      </div>
      <div className="group h-3 overflow-hidden rounded-full bg-gray-100">
        {/* base fill */}
        <div
          className="h-full rounded-full bg-emerald-500"
          style={{ width: play ? `${value}%` : "0%", transition: play ? `width 900ms cubic-bezier(.2,.7,.2,1) ${delay}ms` : "none" }}
        />
        {/* shimmer overlay */}
        <div
          className="pointer-events-none -mt-3 h-3 w-full rounded-full opacity-0 group-hover:opacity-100"
          style={{ background: "linear-gradient(90deg, transparent, rgba(255,255,255,.6), transparent)", animation: play ? `shine 1400ms linear ${delay + 300}ms 2` : "none" }}
        />
      </div>
    </div>
  );
}

function Badge({ label }) {
  return (
    <div className="inline-flex items-center gap-2 rounded-xl border border-gray-200 bg-white px-4 py-2 shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md">
      <ShieldIcon className="h-5 w-5 text-emerald-600" />
      <span className="text-sm font-medium text-gray-800">{label}</span>
    </div>
  );
}

/* ============ Icons ============ */
function QuoteIcon({ className = "" }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M7 7h5v5H7z" />
      <path d="M7 12c0 3 2 5 5 5" />
      <path d="M14 7h5v5h-5z" />
      <path d="M14 12c0 3 2 5 5 5" />
    </svg>
  );
}
function ShieldIcon({ className = "" }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M12 3l8 3v6c0 5-3.5 8-8 10-4.5-2-8-5-8-10V6l8-3z" />
      <path d="M9 12l2 2 4-4" />
    </svg>
  );
}

/* ============ Hooks ============ */
function useInViewOnce({ threshold = 0.35 } = {}) {
  const ref = useRef(null);
  const [seen, setSeen] = useState(false);
  useEffect(() => {
    if (!ref.current || seen) return;
    const io = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting) { setSeen(true); io.disconnect(); }
      },
      { threshold }
    );
    io.observe(ref.current);
    return () => io.disconnect();
  }, [seen, threshold]);
  return { ref, inViewOnce: seen };
}
function usePrefersReducedMotion() {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const on = () => setReduced(!!mq.matches);
    on();
    mq.addEventListener?.("change", on);
    return () => mq.removeEventListener?.("change", on);
  }, []);
  return reduced;
}

/* ============ Extra CSS ============ */
const extraCSS = `
@keyframes shine { 0% { transform: translateX(-100%); } 100% { transform: translateX(100%); }}
`;
